import { authFetch, buildWorkspaceUrl, throwApiError, unwrapObject } from './core'
import { requestOptions } from './workspace'

export interface UploadedFile {
  id: string
  originalName: string
  storageName: string
  fileType: string
  fileSize: number
  storagePath: string
  videoDuration?: number | null
  videoThumbnailId?: string | null
  videoThumbnailUrl?: string | null
  videoResolution?: string | null
  createdAt?: string | null
  updatedAt?: string | null
}

interface UploadFileOptions {
  onProgress?: (percent: number) => void
  signal?: AbortSignal
}

export async function uploadFile(token: string, file: File, options?: UploadFileOptions): Promise<UploadedFile> {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('enableCompression', 'true')

  if (typeof XMLHttpRequest === 'undefined') {
    const response = await authFetch(buildWorkspaceUrl('/api/storage/v1/files'), {
      method: 'POST',
      body: formData,
      ...requestOptions(token),
    })

    if (!response.ok) {
      await throwApiError(response, 'Failed to upload attachment')
    }

    const json = await response.json()
    return unwrapObject<UploadedFile>(json, ['file', 'data', 'item'])
  }

  return new Promise<UploadedFile>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', buildWorkspaceUrl('/api/storage/v1/files'))
    xhr.withCredentials = true

    const requestInit = requestOptions(token)
    const headers = requestInit.headers as Record<string, string> | undefined
    Object.entries(headers ?? {}).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value)
    })

    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable) return
      options?.onProgress?.(Math.round((event.loaded * 100) / event.total))
    }

    xhr.onload = () => {
      const responseText = xhr.responseText
      let json: unknown = null
      try {
        json = responseText ? JSON.parse(responseText) : null
      } catch {
        json = null
      }

      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(unwrapObject<UploadedFile>(json, ['file', 'data', 'item']))
        return
      }

      const fallbackResponse = new Response(responseText, { status: xhr.status, statusText: xhr.statusText })
      throwApiError(fallbackResponse, 'Failed to upload attachment').catch(reject)
    }

    xhr.onerror = () => {
      reject(new Error('Failed to upload attachment'))
    }

    xhr.onabort = () => {
      reject(new DOMException('Upload aborted', 'AbortError'))
    }

    if (options?.signal) {
      options.signal.addEventListener('abort', () => xhr.abort(), { once: true })
    }

    xhr.send(formData)
  })
}
